// /users — búsqueda de usuarios + ruta al detalle.
//
// Dos formas de buscar, cada una con su campo, porque tienen contratos
// distintos y mezclarlos en uno solo obligaba a adivinar:
//
//   1. Exacta (email, documento o user_id). El operador pega lo que le
//      dictaron por teléfono y el backend deduce el criterio (ver
//      searchUsersAdmin). Resuelve por igualdad contra un hash — email y
//      documento están cifrados en Aurora — así que devuelve la cuenta o
//      nada, y con una sola coincidencia abre la ficha directamente.
//
//   2. Por nombre. Coincide por el comienzo del apellido, y del nombre si se
//      agrega. No es exacta ni acotada por naturaleza, así que pagina por
//      cursor y se lista siempre, aunque haya una sola: por nombre no hay
//      certeza, y quien atiende confirma con el email antes de entrar.
//
// Una sola lista de resultados para las dos: es la misma fila con las mismas
// pastillas, y el encabezado dice por cuál búsqueda salió.

import { FormEvent, useRef, useState } from 'react';
import { Link, Route, Routes, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  IdCard,
  Mail,
  Search,
  ShieldAlert,
  UserRound,
  UserSearch,
} from 'lucide-react';
import {
  searchUsers,
  searchUsersByName,
  AdminApiError,
  type AdminUserSummary,
  type SearchCriterion,
  type UserNamePage,
} from '@/lib/api';
import { relativeTime, shortDate, absoluteTime } from '@/lib/format';
import { Pill } from '@/components/DataCard';
import { SUB_LABEL, SUB_TONE } from '@/lib/subscription-status';
import UserDetail from './UserDetail';
import { fieldCls } from '@/lib/form-styles';

const CRITERION_LABEL: Record<SearchCriterion, string> = {
  email: 'email',
  document: 'documento',
  user_id: 'user_id',
};

/** Botón primario de los dos formularios. Un solo lugar para las dos clases. */
const SUBMIT_CLS =
  'min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white font-medium px-6 py-2 focus-visible:ring-2 focus-visible:ring-brand-light disabled:opacity-50 disabled:cursor-not-allowed';

/** Botón secundario (paginación). */
const PAGER_CLS =
  'inline-flex items-center gap-1 rounded-lg border border-surface-border dark:border-surface-dark-border bg-surface dark:bg-surface-dark px-3 py-1.5 text-sm font-medium hover:border-brand focus-visible:ring-2 focus-visible:ring-brand-light disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-surface-border dark:disabled:hover:border-surface-dark-border';

/**
 * "Apellido, Nombre" cuando la cuenta tiene el apellido separado; si no, lo
 * que haya. Es el orden en que están ordenados los resultados por nombre, así
 * que la lista se lee como una guía telefónica.
 */
function formalName(u: AdminUserSummary): string {
  if (u.last_name) {
    return u.first_name ? `${u.last_name}, ${u.first_name}` : u.last_name;
  }
  return u.display_name ?? u.first_name ?? 'Usuario sin nombre';
}

type ResultState =
  | { kind: 'exact'; criterion: SearchCriterion; users: AdminUserSummary[]; term: string }
  | { kind: 'name'; term: string; users: AdminUserSummary[]; page: UserNamePage['page'] };

function UserSearchPage(): JSX.Element {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [nameTerm, setNameTerm] = useState('');
  // Un solo indicador de carga para las dos búsquedas: nunca corren a la vez y
  // así los dos botones se deshabilitan juntos.
  const [loading, setLoading] = useState<'exact' | 'name' | 'page' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<ResultState | null>(null);
  const resultsRef = useRef<HTMLElement | null>(null);

  async function onSubmitExact(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const value = term.trim();
    if (!value) return;

    setLoading('exact');
    setErr(null);
    setResult(null);
    try {
      const res = await searchUsers(value);
      // Un único match va directo al detalle: en una llamada, un click de menos
      // importa. Con varios (típico al buscar por documento) se muestra la
      // lista para que el operador elija.
      if (res.users.length === 1) {
        navigate(`/users/${res.users[0]!.user_id}`);
        return;
      }
      setResult({ kind: 'exact', ...res, term: value });
    } catch (e) {
      const code = e instanceof AdminApiError ? e.code : 'unknown';
      setErr(
        code === 'invalid_request_body'
          ? 'El término de búsqueda no es válido.'
          : 'No pudimos completar la búsqueda.',
      );
    } finally {
      setLoading(null);
    }
  }

  async function runNameSearch(
    value: string,
    cursor?: { after?: string; before?: string },
  ): Promise<void> {
    setLoading(cursor ? 'page' : 'name');
    setErr(null);
    // Al paginar se conserva la lista anterior hasta que llega la nueva: así
    // el bloque no se vacía ni salta de alto entre una página y otra.
    if (!cursor) setResult(null);
    try {
      const res = await searchUsersByName(value, cursor);
      setResult({ kind: 'name', term: res.term, users: res.users, page: res.page });
      if (cursor) {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (e) {
      const code = e instanceof AdminApiError ? e.code : 'unknown';
      setErr(
        code === 'invalid_request_body'
          ? 'Escribí al menos 3 letras del apellido.'
          : code === 'rate_limited'
            ? 'Demasiadas búsquedas seguidas. Esperá un minuto.'
            : 'No pudimos completar la búsqueda.',
      );
    } finally {
      setLoading(null);
    }
  }

  async function onSubmitName(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const value = nameTerm.trim();
    if (!value) return;
    await runNameSearch(value);
  }

  const busy = loading !== null;

  return (
    <div className="space-y-6">
      <header>
        <h1 data-help-page="users.page" data-help="users.page" className="text-2xl font-semibold">
          Usuarios
        </h1>
        <p className="text-sm text-ink-muted max-w-2xl">
          Dos formas de encontrar una cuenta. Con el <strong>email</strong>, el{' '}
          <strong>documento</strong> o el <strong>user_id</strong> la coincidencia es exacta y, si
          hay una sola, se abre directo. Con el <strong>apellido</strong> se busca por el comienzo
          y se listan todas las que empiecen igual.
        </p>
      </header>

      {/* ---- Búsqueda exacta ------------------------------------------------ */}
      <form
        onSubmit={onSubmitExact}
        data-help="users.search"
        aria-busy={loading === 'exact' ? 'true' : undefined}
        className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end max-w-2xl"
      >
        <label htmlFor="search-term" className="flex-1 text-sm space-y-1">
          <span className="font-medium">Email, documento o user_id</span>
          <div className="relative">
            <Search
              size={16}
              aria-hidden="true"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle"
            />
            <input
              id="search-term"
              type="text"
              required
              autoFocus
              autoComplete="off"
              spellCheck={false}
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="juan@ejemplo.com · 30123456 · 018f…"
              className={fieldCls({ size: 'lg', extra: 'pl-9 pr-3' })}
            />
          </div>
        </label>
        <button type="submit" disabled={busy} className={SUBMIT_CLS}>
          {loading === 'exact' ? 'Buscando…' : 'Buscar'}
        </button>
      </form>

      {/* ---- Búsqueda por nombre -------------------------------------------- */}
      <section
        aria-labelledby="name-search-title"
        data-help="users.search-name"
        className="max-w-2xl rounded-xl border border-surface-border dark:border-surface-dark-border bg-surface dark:bg-surface-dark p-4 space-y-3"
      >
        <div>
          <h2 id="name-search-title" className="flex items-center gap-2 font-medium">
            <UserSearch size={16} className="text-ink-subtle" aria-hidden="true" />
            Buscar por nombre
          </h2>
          <p className="text-sm text-ink-muted">
            Coincide por el <strong>comienzo del apellido</strong>. Agregá el nombre para achicar la
            lista. No importan las tildes ni las mayúsculas.
          </p>
        </div>
        <form
          onSubmit={onSubmitName}
          aria-busy={loading === 'name' ? 'true' : undefined}
          className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end"
        >
          <label htmlFor="search-name" className="flex-1 text-sm space-y-1">
            <span className="font-medium">Apellido y nombre</span>
            <div className="relative">
              <UserRound
                size={16}
                aria-hidden="true"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle"
              />
              <input
                id="search-name"
                type="text"
                required
                autoComplete="off"
                spellCheck={false}
                value={nameTerm}
                onChange={(e) => setNameTerm(e.target.value)}
                placeholder="Pérez Juan · García"
                className={fieldCls({ size: 'lg', extra: 'pl-9 pr-3' })}
              />
            </div>
          </label>
          <button type="submit" disabled={busy} className={SUBMIT_CLS}>
            {loading === 'name' ? 'Buscando…' : 'Buscar'}
          </button>
        </form>
      </section>

      {err ? (
        <div
          role="alert"
          className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error max-w-2xl"
        >
          {err}
        </div>
      ) : null}

      {result ? (
        result.users.length === 0 ? (
          <EmptyResult result={result} />
        ) : (
          <section
            ref={resultsRef}
            className="space-y-2 max-w-3xl scroll-mt-4"
            data-help="users.results"
            aria-busy={loading === 'page' ? 'true' : undefined}
          >
            <ResultHeading result={result} />
            {/* El paginador va arriba Y abajo: con cincuenta filas, el de abajo
                queda a dos pantallas de distancia y el operador no se entera
                de que hay más páginas hasta que scrollea. El de arriba es el
                que lleva la ayuda y el recorrido. */}
            {result.kind === 'name' ? (
              <Pager
                result={result}
                busy={busy}
                withHelp
                onPrev={() => runNameSearch(result.term, { before: result.page.prev! })}
                onNext={() => runNameSearch(result.term, { after: result.page.next! })}
              />
            ) : null}
            <ul
              className={`space-y-2 transition-opacity ${loading === 'page' ? 'opacity-50' : ''}`}
            >
              {result.users.map((u) => (
                <li key={u.user_id}>
                  <Link
                    to={`/users/${u.user_id}`}
                    className="block bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border p-3 hover:border-brand focus-visible:ring-2 focus-visible:ring-brand-light"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <UserRound size={16} className="text-ink-subtle shrink-0" aria-hidden="true" />
                      <span className="font-medium truncate">
                        {result.kind === 'name'
                          ? formalName(u)
                          : (u.display_name ?? 'Usuario sin nombre')}
                      </span>
                      {u.status === 'blocked' ? (
                        <Pill tone="error">
                          <ShieldAlert size={11} aria-hidden="true" />
                          bloqueada
                        </Pill>
                      ) : null}
                      {u.email_verified === false ? (
                        <Pill tone="warning">
                          <Mail size={11} aria-hidden="true" />
                          sin verificar
                        </Pill>
                      ) : null}
                      {u.subscription_status ? (
                        <Pill tone={SUB_TONE[u.subscription_status]}>
                          {u.subscription_status === 'active'
                            ? 'suscripción activa'
                            : SUB_LABEL[u.subscription_status]}
                        </Pill>
                      ) : (
                        <Pill tone="neutral">sin suscripción</Pill>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink-muted">
                      <span className="truncate">{u.email}</span>
                      {u.document_masked ? (
                        <span className="inline-flex items-center gap-1">
                          <IdCard size={12} aria-hidden="true" />
                          {u.document_masked}
                        </span>
                      ) : null}
                      {u.partner_id ? <span>{u.partner_id}</span> : null}
                      <span className="ml-auto" title={absoluteTime(u.created_at)}>
                        alta {shortDate(u.created_at)} ({relativeTime(u.created_at)})
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            {result.kind === 'name' ? (
              <Pager
                result={result}
                busy={busy}
                onPrev={() => runNameSearch(result.term, { before: result.page.prev! })}
                onNext={() => runNameSearch(result.term, { after: result.page.next! })}
              />
            ) : null}
          </section>
        )
      ) : null}
    </div>
  );
}

function ResultHeading({ result }: { result: ResultState }): JSX.Element {
  if (result.kind === 'name') {
    return (
      <h2 className="text-sm text-ink-muted">
        Cuentas cuyo apellido empieza con <strong>«{result.term}»</strong>, en orden alfabético.
        {result.page.prev ? null : result.page.next ? null : (
          <span className="ml-1">Es la lista completa.</span>
        )}
      </h2>
    );
  }
  return (
    <h2 className="text-sm text-ink-muted">
      {result.users.length} cuenta(s) con ese {CRITERION_LABEL[result.criterion]}
      {result.criterion === 'document' ? (
        <span className="ml-1">— un mismo pagador puede sostener varias suscripciones.</span>
      ) : null}
    </h2>
  );
}

function EmptyResult({ result }: { result: ResultState }): JSX.Element {
  return (
    <div
      role="status"
      className="rounded-lg border border-surface-border dark:border-surface-dark-border p-4 text-sm max-w-2xl space-y-2"
    >
      <p className="flex items-center gap-2 font-medium">
        <AlertTriangle size={16} className="text-status-warning" aria-hidden="true" />
        {result.kind === 'name' ? (
          // Un solo hijo de texto: el <p> es flex y cada fragmento suelto seria
          // un item separado por el gap (se leia "con  «x» ." con espacios).
          <span>
            No hay cuentas cuyo apellido empiece con{' '}
            <span className="font-semibold">«{result.term}»</span>.
          </span>
        ) : (
          'No encontramos ninguna cuenta.'
        )}
      </p>
      {result.kind === 'name' ? (
        <p className="text-ink-muted">
          Probá con el apellido solo, por si el nombre está cargado distinto, o pedí el email: es lo
          que la persona usa para entrar.
        </p>
      ) : (
        <p className="text-ink-muted">
          Se buscó por <strong>{CRITERION_LABEL[result.criterion]}</strong>, con coincidencia exacta.
          {result.criterion === 'document' ? (
            <>
              {' '}
              Tené en cuenta que el documento solo queda registrado si la persona pagó con tarjeta:
              quien nunca contrató no se puede encontrar así.
            </>
          ) : result.criterion === 'email' ? (
            <> Revisá que no falte o sobre alguna letra en el dominio.</>
          ) : null}
        </p>
      )}
    </div>
  );
}

/**
 * Barra de paginación por cursor. No hay "página 3 de 47": el backend no cuenta
 * el total a propósito. Lo que orienta es el rango alfabético de la página —
 * como el encabezado de una guía telefónica — y la pista de que hay más.
 */
function Pager({
  result,
  busy,
  withHelp = false,
  onPrev,
  onNext,
}: {
  result: Extract<ResultState, { kind: 'name' }>;
  busy: boolean;
  /** Solo una de las dos instancias lleva el punto de ayuda. */
  withHelp?: boolean;
  onPrev: () => void;
  onNext: () => void;
}): JSX.Element | null {
  const { prev, next } = result.page;
  if (!prev && !next) return null;

  const first = result.users[0]!;
  const last = result.users[result.users.length - 1]!;

  return (
    <nav
      aria-label="Páginas de resultados"
      data-help={withHelp ? 'users.pagination' : undefined}
      className="rounded-xl border border-surface-border dark:border-surface-dark-border bg-surface dark:bg-surface-dark p-3 flex flex-col sm:flex-row sm:items-center gap-3"
    >
      <div className="flex-1 text-sm min-w-0">
        <p className="truncate">
          En esta página: de <strong>{formalName(first)}</strong> a <strong>{formalName(last)}</strong>
        </p>
        {next ? (
          <p className="text-ink-muted">Hay más cuentas: agregá el nombre para achicar la lista.</p>
        ) : (
          <p className="text-ink-muted">Es la última página.</p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button type="button" onClick={onPrev} disabled={!prev || busy} className={PAGER_CLS}>
          <ChevronLeft size={16} aria-hidden="true" />
          Anterior
        </button>
        <button type="button" onClick={onNext} disabled={!next || busy} className={PAGER_CLS}>
          Siguiente
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
}

export default function Users(): JSX.Element {
  return (
    <Routes>
      <Route index element={<UserSearchPage />} />
      <Route path=":userId" element={<UserDetail />} />
    </Routes>
  );
}
